export function sourceLabel(source) {
  const method = source?.source_method || 'unknown';
  const sec = source?.secondary_data_provider_abbreviation;
  const dp = source?.data_provider;
  if (sec) return `${method} / ${sec}`;
  if (dp) return `${method} / ${dp}`;
  return method;
}

// Normalize an ATP/ECO curie to canonical uppercase form so case differences
// between ES bucket keys, TET DB rows, and the ATP cache don't cause silent
// mismatches. (Existing TopicEntityTable.js does the same via bucket.key.toUpperCase().)
export function normalizeCurie(curie) {
  if (!curie) return curie;
  return String(curie).toUpperCase();
}

function asTets(value) {
  if (Array.isArray(value)) return value;
  return value?.tets || [];
}

export function groupTetsByTopicAndSource(tets) {
  const byTopic = new Map();
  for (const tet of asTets(tets)) {
    const topicKey = normalizeCurie(tet.topic);
    const label = sourceLabel(tet.tag_source);
    if (!byTopic.has(topicKey)) byTopic.set(topicKey, new Map());
    const bySource = byTopic.get(topicKey);
    if (!bySource.has(label)) bySource.set(label, []);
    bySource.get(label).push(tet);
  }
  return byTopic;
}

/** The only curator validation_type. The old 'professional_curator' spelling
 *  never matched what validation edges key on and is normalised away by the
 *  SCRUM-6518 migration, which ships ahead of this code. Named to match the
 *  API's CURATOR_VALIDATION_TYPE in tag_source_crud.py. */
const CURATOR_VALIDATION_TYPE = 'professional_biocurator';

/** True for any TET whose source is a professional-biocurator source —
 *  regardless of entity. Useful to hide curator-submitted rows from the
 *  Sources column, since they already surface in the Validation column. */
export function isCuratorSourceTet(tet) {
  return (
    !!tet &&
    tet?.tag_source?.validation_type === CURATOR_VALIDATION_TYPE
  );
}

export function isCuratorValidationTet(tet) {
  return (
    !!tet &&
    !tet.entity &&
    tet.tag_source?.validation_type === CURATOR_VALIDATION_TYPE
  );
}

export function cellSortRank(tets) {
  const arr = asTets(tets);
  if (arr.length === 0) return 0;
  if (arr.some((t) => t.negated === false)) return 2;
  return 1;
}

/** Server-aggregated validation carried on a cell value object, or `undefined`
 *  when the value is a bare tets array / the row was not server-aggregated (older
 *  backend or per-reference fallback) and the state must be derived from raw
 *  tets. A value of `null` means the server DID aggregate the row but found no
 *  curator validation for this topic (=> 'unvalidated'). */
function cellServerValidation(value) {
  if (!value || Array.isArray(value)) return undefined;
  return value.validation;
}

/** Validation state of a (reference, topic) cell, considering only
 *  professional-biocurator topic-level TETs.
 *  Returns one of: 'unvalidated' | 'positive' | 'negative' | 'conflict'.
 *  Prefers the server aggregate (value.validation.state) when present so the
 *  grid sorts/filters without re-deriving from raw tags; falls back to deriving
 *  from raw tets when the value carries no aggregate. */
export function validationState(tets) {
  const sv = cellServerValidation(tets);
  if (sv !== undefined) return sv && sv.state ? sv.state : 'unvalidated';
  const validations = asTets(tets).filter(isCuratorValidationTet);
  if (validations.length === 0) return 'unvalidated';
  const hasPos = validations.some((t) => !t.negated);
  const hasNeg = validations.some((t) => t.negated);
  if (hasPos && hasNeg) return 'conflict';
  return hasPos ? 'positive' : 'negative';
}

export const VALIDATION_FILTER_KEYS = [
  'unvalidated',
  'positive',
  'negative',
  'conflict',
];

/** Display labels for the validation states above. The keys are the server's
 *  aggregate `validation.state` values and are also what gets persisted in
 *  saved AgGrid filter models, so they must not change — only the UI wording
 *  does. Y / N mirror the "Data" column pills and the `has_data` advanced-search
 *  facet, so a biocurator assessment reads the same way everywhere (SCRUM-6330). */
export const VALIDATION_STATE_LABELS = Object.freeze({
  unvalidated: 'unassessed',
  positive: 'Y',
  negative: 'N',
  conflict: 'conflict',
});

/** Human label for a validation state, falling back to the raw key so an
 *  unrecognized server state still renders something. */
export function validationStateLabel(state) {
  return VALIDATION_STATE_LABELS[state] || state;
}

/** Stable numeric ordering for AgGrid sort: unvalidated < conflict <
 *  negative < positive. Curators can flip via shift-click for descending. */
export function validationSortRank(tets) {
  const s = validationState(tets);
  return { unvalidated: 0, conflict: 1, negative: 2, positive: 3 }[s] || 0;
}

export const TOPIC_CELL_FILTER_KEYS = [
  'has any tag',
  'has Y',
  'has N',
  'has note',
  'my validation present',
  'empty',
];

export function cellPredicate(tets, currentUid, model) {
  if (!Array.isArray(model) || model.length === 0) return true;
  const arr = asTets(tets);
  // Prefer the server-aggregated per-cell flags (has_any/has_y/has_n/has_note/
  // my_validation_present) when present so filtering doesn't scan raw tags.
  // `null`/`undefined` => no server aggregate, derive from raw tets. The server
  // computes my_validation_present by comparing each tag's created_by against
  // the authenticated user's internal users.id -- the comparison the client
  // cannot do reliably, since its uid is an Okta subject id and the serialized
  // created_by is a display name. The raw-tets fallback keeps the older-backend
  // path working.
  const flags =
    tets && !Array.isArray(tets) ? tets.filterFlags : undefined;
  const server = flags != null;
  return model.some((key) => {
    switch (key) {
      case 'empty':
        return server ? !flags.has_any : arr.length === 0;
      case 'has any tag':
        return server ? !!flags.has_any : arr.length > 0;
      case 'has Y':
        return server ? !!flags.has_y : arr.some((t) => t.negated === false);
      case 'has N':
        return server ? !!flags.has_n : arr.some((t) => t.negated === true);
      case 'has note':
        return server
          ? !!flags.has_note
          : arr.some((t) => t.note != null && t.note !== '');
      case 'my validation present':
        return server
          ? !!flags.my_validation_present
          : arr.some((t) => t.created_by === currentUid);
      default:
        return false;
    }
  });
}
